export const solutionPages = [
  {
    slug: 'bpo',
    name: 'BPO',
    shortDescription: 'Automate outsourced operations end-to-end while improving quality and margin.',
    overview:
      'Deploy AI agents across customer service, document handling, and admin-heavy shared-service workflows so delivery teams can scale output without linear headcount growth.',
    workflows: [
      'Omnichannel intake and routing across email, voice, and chat',
      'Tier-1 customer support, ticket updates, and follow-up execution',
      'Document parsing, data extraction, and quality-control checks',
      'Back-office scheduling, status updates, and client reporting',
      'Escalation handoffs for policy exceptions and complex cases',
    ],
    outcomes: [
      'Shorter turnaround times for repetitive operational work',
      'Reduced operating cost per client workflow',
      'Lower error rates in structured processing tasks',
      '24/7 operational coverage for high-volume queues',
      'Consistent service quality across teams and shifts',
      'Higher delivery capacity without proportional hiring',
    ],
    proofPoints: [
      { value: '<2 min', label: 'Typical execution time for routine processing tasks' },
      { value: '70%', label: 'Potential reduction in operating cost on repetitive workflows' },
      { value: '95%', label: 'Fewer manual processing errors in governed flows' },
    ],
    useCases: [
      'Customer-service inbox triage and response orchestration',
      'Data-entry and reconciliation support for shared services',
      'Document review pipelines with auto-classification and extraction',
      'Back-office QA and compliance spot-check workflows',
    ],
  },
  {
    slug: 'hr-recruitment',
    name: 'HR & Recruitment',
    shortDescription: 'Run staffing and RPO workflows faster with AI-led screening and coordination.',
    overview:
      'Recruiting teams often spend most of their time on repetitive screening, formatting, and follow-ups. AI agents automate those operational loops so recruiters can focus on final evaluation and stakeholder alignment.',
    workflows: [
      'Resume parsing, scoring, and shortlist preparation',
      'Candidate outreach, reminders, and follow-up sequencing',
      'Interview coordination and schedule management',
      'Hiring-manager updates, approvals, and status notifications',
      'Compliance-aware candidate data capture and handoff',
    ],
    outcomes: [
      'Faster submission cycles for open roles',
      'Reduced admin workload for recruiters and sourcers',
      'Higher candidate response consistency across stages',
      'Improved hiring-manager visibility and trust',
      'Less scheduling friction across interview loops',
      'Stronger candidate experience at scale',
    ],
    proofPoints: [
      { value: '50%', label: 'Time-to-hire reduction potential on repeatable role funnels' },
      { value: '24/7', label: 'Always-on candidate communication and workflow progression' },
      { value: '70%', label: 'Recruiter admin burden addressable through workflow automation' },
    ],
    useCases: [
      'Resume review and candidate ranking against role criteria',
      'Automated interview scheduling across recruiter and panel calendars',
      'Client-submission formatting and consistency checks for RPO teams',
      'Candidate pipeline nudges to reduce drop-off before interviews',
    ],
  },
  {
    slug: 'finance-services',
    name: 'Financial Services',
    shortDescription: 'Automate finance operations with audit-ready, policy-aware AI execution.',
    overview:
      'Use AI agents to orchestrate high-volume financial workflows across reconciliation, reporting, customer operations, and compliance while preserving traceability and control.',
    workflows: [
      'Invoice processing, reconciliation, and reporting support',
      'KYC and document validation workflow execution',
      'Fraud, AML, and risk-monitoring signal triage',
      'Customer account service and request automation',
      'Audit-trail generation for regulated finance operations',
    ],
    outcomes: [
      'Faster close and back-office processing cycles',
      'Reduced operational cost and manual rework',
      'Improved accuracy in repetitive finance workflows',
      'Stronger compliance consistency across teams',
      'Higher throughput without sacrificing controls',
      'Real-time operational visibility for finance leaders',
    ],
    proofPoints: [
      { value: '<1 min', label: 'Routine task execution in structured finance workflows' },
      { value: '70%', label: 'Operating-cost reduction potential for repetitive finance tasks' },
      { value: '95%', label: 'Lower manual-error exposure in automated process lanes' },
    ],
    useCases: [
      'Accounts receivable and payment matching automation',
      'Regulatory reporting preparation and compliance checks',
      'Transaction anomaly triage and escalation routing',
      'Client-service operations for account and policy updates',
    ],
  },
  {
    slug: 'customer-service',
    name: 'Customer Service',
    shortDescription: 'Resolve routine tickets instantly while your team handles high-value interactions.',
    overview:
      'Run support operations with AI agents that answer quickly, execute actions reliably, and escalate edge cases with full context across email, chat, and voice.',
    workflows: [
      'Instant FAQ and policy-grounded customer responses',
      'Order, billing, cancellation, and refund request handling',
      'Inbox triage with intent classification and prioritization',
      'Agent-assist recommendations for complex service cases',
      'Context-rich escalations to human support specialists',
    ],
    outcomes: [
      'Faster first response and reduced queue latency',
      'Higher resolution rate for repetitive ticket categories',
      'Lower agent load on routine support work',
      'Consistent service quality across channels and regions',
      'Improved customer satisfaction on high-volume requests',
      'More time for human agents to solve complex issues',
    ],
    proofPoints: [
      { value: '<1 min', label: 'Typical response time for routine support requests' },
      { value: '80%', label: 'Agent time savings on repetitive support interactions' },
      { value: '95%', label: 'Routine ticket categories eligible for autonomous handling' },
    ],
    useCases: [
      'Order status, returns, and refund request handling',
      'Subscription and account servicing workflows',
      'Email triage and response orchestration for support inboxes',
      'Multilingual customer support with policy-consistent answers',
    ],
  },
  {
    slug: 'debt-collection',
    name: 'Debt Collection',
    shortDescription: 'Increase recoveries with compliant, automated outreach and repayment workflows.',
    overview:
      'Automate debt-recovery operations from account prioritization to repayment follow-up, while maintaining compliant communication and clear escalation controls.',
    workflows: [
      'Overdue account triage and outreach sequencing',
      'Payment reminder campaigns across email, SMS, and voice',
      'Repayment-plan communication and status tracking',
      'Collector assist with next-best-action recommendations',
      'Escalation lanes for legal, risk, and dispute cases',
    ],
    outcomes: [
      'Faster time-to-recovery across delinquent segments',
      'Higher consistency in outreach cadence and follow-up',
      'Lower manual workload for collector teams',
      'Improved debtor communication experience',
      'Reduced recovery cost on repetitive tasks',
      'Better operational visibility into collection performance',
    ],
    proofPoints: [
      { value: '72%', label: 'Revenue recovery uplift target from optimized debt workflows' },
      { value: '<1 min', label: 'Execution time for routine collection workflow actions' },
      { value: '70%', label: 'Potential operating-cost reduction in collection operations' },
    ],
    useCases: [
      'Automated payment reminders with channel-based cadence logic',
      'Debtor segmentation and prioritized outreach orchestration',
      'Promise-to-pay tracking and repayment lifecycle updates',
      'Escalation routing for disputes and legal-sensitive accounts',
    ],
  },
  {
    slug: 'healthcare',
    name: 'Healthcare',
    shortDescription: 'Reduce wait times and administrative burden across patient-service workflows.',
    overview:
      'Healthcare teams use AI agents to automate intake, scheduling, documentation, and service coordination, so clinicians and staff can focus on patient care.',
    workflows: [
      'Patient intake, triage, and scheduling support',
      'Insurance verification and claims workflow assistance',
      'Medical and lab report data extraction and routing',
      'Appointment reminders, follow-ups, and rescheduling flows',
      'Patient service communications across channels and languages',
    ],
    outcomes: [
      'Shorter wait times for patient responses and coordination',
      'Reduced administrative load for care operations teams',
      'Improved consistency in patient communication quality',
      'Lower operational friction in non-clinical workflows',
      'Higher throughput across high-volume intake queues',
      'More time for providers to focus on direct care',
    ],
    proofPoints: [
      { value: '70%', label: 'Potential reduction in patient wait times for service workflows' },
      { value: '<1 min', label: 'Routine task execution for structured healthcare operations' },
      { value: '24/7', label: 'Always-on multilingual support for patient-facing requests' },
    ],
    useCases: [
      'Appointment scheduling and reminder automation',
      'Patient onboarding and intake documentation workflows',
      'Claims and insurance support process coordination',
      'Lab and medical report extraction into downstream systems',
    ],
  },
  {
    slug: 'talent-acquisition',
    name: 'Talent Acquisition',
    shortDescription: 'Fill roles faster with AI agents that run sourcing-to-scheduling operations.',
    overview:
      'TA teams can deploy AI agents for sourcing support, screening operations, interview logistics, and candidate engagement to accelerate hiring cycles without adding admin overhead.',
    workflows: [
      'Candidate sourcing enrichment and prioritization',
      'Screening, submittal preparation, and fit ranking',
      'Interview scheduling and stakeholder coordination',
      'Candidate communication cadence and follow-up automation',
      'Hiring-manager status updates and approval routing',
    ],
    outcomes: [
      'Faster movement from application to interview',
      'Reduced recruiter hours spent on repetitive coordination',
      'Higher consistency in candidate communication quality',
      'Less candidate drop-off across interview stages',
      'Improved hiring-manager visibility into pipeline health',
      'Better conversion from shortlist to accepted offer',
    ],
    proofPoints: [
      { value: '50%', label: 'Potential reduction in time-to-fill for repeat role families' },
      { value: '792h', label: 'Recruiter hours reclaimed per hire cycle in large funnels' },
      { value: '24/7', label: 'Continuous candidate engagement and process progression' },
    ],
    useCases: [
      'Resume parsing and quality scoring at inbound scale',
      'Interview coordination across multi-panel hiring loops',
      'Automated candidate nudges to reduce no-shows and drop-off',
      'Hiring-process bottleneck tracking with actionable insight',
    ],
  },
  {
    slug: 'insurance',
    name: 'Insurance',
    shortDescription: 'Process claims and policy operations faster with compliant AI workflows.',
    overview:
      'Insurance teams can automate claims intake, policy servicing, customer communication, and compliance-heavy workflows on a secure AI operating layer.',
    workflows: [
      'Claims intake, triage, and status communication workflows',
      'Policy servicing and customer request handling',
      'KYC/AML and documentation support automation',
      'Payment reconciliation and claims follow-up flows',
      'Escalation logic for underwriting and risk exceptions',
    ],
    outcomes: [
      'Claims processed in hours instead of multi-week cycles',
      'Faster servicing response for policyholders',
      'Reduced administrative workload in claims operations',
      'Stronger compliance consistency in high-volume tasks',
      'Lower cost-to-serve across repetitive request categories',
      'Higher service quality across cross-channel interactions',
    ],
    proofPoints: [
      { value: 'Hours', label: 'Claim processing target instead of legacy multi-week cycles' },
      { value: '<1 min', label: 'Execution speed for routine insurance workflow actions' },
      { value: '70%', label: 'Potential reduction in operational cost for repetitive flows' },
    ],
    useCases: [
      'FNOL intake and policy verification routing',
      'Claims status communication and missing-document follow-up',
      'KYC and compliance checkpoint orchestration',
      'Support inbox automation for insurer and MGA operations',
    ],
  },
  {
    slug: 'property-management',
    name: 'Property Management',
    shortDescription: 'Run tenant, maintenance, and collection workflows at portfolio scale with AI.',
    overview:
      'Property teams can automate tenant communication, maintenance triage, collections support, and document-heavy workflows while keeping service levels high across growing portfolios.',
    workflows: [
      'Tenant inquiry triage and response coordination',
      'Maintenance intake, assignment, and follow-up updates',
      'Collections communication and payment reminders',
      'Compliance and ARC process support workflows',
      'Property data handling for underwriting and reporting',
    ],
    outcomes: [
      'Faster tenant response times across common requests',
      'Lower admin burden for property operations teams',
      'More predictable maintenance coordination cycles',
      'Improved consistency in service quality across units',
      'Reduced operating cost on repetitive management tasks',
      'Portfolio growth without equivalent staffing expansion',
    ],
    proofPoints: [
      { value: '500+', label: 'Units manageable with lean teams using automated workflows' },
      { value: '<1 min', label: 'Execution speed for routine property operations tasks' },
      { value: '70%', label: 'Potential operating-cost reduction in repetitive workflows' },
    ],
    useCases: [
      'Tenant inquiry management and automated follow-up loops',
      'Property damage intake, assessment routing, and resolution updates',
      'Maintenance request orchestration across vendors and staff',
      'Collections support and lease lifecycle communication',
    ],
  },
  {
    slug: 'custom-ai-solutions',
    name: 'Custom AI Solutions',
    shortDescription: 'Build tailored AI systems for specialized processes and compliance-heavy operations.',
    overview:
      'When off-the-shelf automations cannot model your process depth, custom AI agents are designed around your terminology, systems, governance constraints, and operating model.',
    workflows: [
      'Industry-specific AI model customization and evaluation',
      'Custom copilots, chatbots, and virtual assistant design',
      'Workflow automation across finance, operations, and service teams',
      'Generative AI pipelines for summarization and data processing',
      'Governance, monitoring, and rollout controls for production use',
    ],
    outcomes: [
      'Automation aligned to your exact process requirements',
      'Reduced operational drag in complex multi-step workflows',
      'Higher fit for regulated or domain-specific environments',
      'Improved scalability with enterprise-grade control points',
      'Faster delivery of measurable business impact from AI',
      'Future-ready architecture for continuous capability expansion',
    ],
    proofPoints: [
      { value: '5', label: 'Core industry tracks pre-modeled for custom deployment patterns' },
      { value: '1000+', label: 'Integration pathways and connectors to existing business systems' },
      { value: 'SOC 2', label: 'Security and governance baseline for enterprise implementations' },
    ],
    useCases: [
      'Sales and marketing personalization and campaign orchestration',
      'Finance and accounting automation with anomaly detection',
      'Healthcare and insurance workflow acceleration',
      'HR and legal document processing with compliance controls',
    ],
  },
];

export const solutionPagesBySlug = Object.fromEntries(
  solutionPages.map((solution) => [solution.slug, solution]),
);
